import React, { useEffect, useState } from 'react';
import { Flags } from 'react-feature-flags';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';

import { Loader, useMatomo } from '@omnigenbiodata/ui';
import { useTranslation } from 'react-i18next';

import PortalLayout from '../../../../layout/Portal';
import { useAppSelector, useAppDispatch } from '../../../../store';
import { getConsentFormLinkThunk, updateMyProfileThunk } from '../../../../store/participant/';
import {
  consentFormLinkSelector,
  profileSelector,
  hasErrorSelector,
  isBusySelector,
  isConsentFormBusySelector,
  hasConsentFormErrorSelector,
} from '../../../../store/participant/selectors';

import DetailsUpdateForm from './components/DetailsUpdateForm';
import { FEATURES } from '../../../../core/constants/features.constants';
import { chartColours } from '../../../../core/constants/colours.constants';
import PageContainer from '../../../../components/content/PageContainer';

function PersonalScene() {
  const { trackEvent } = useMatomo();
  const { t } = useTranslation(['portal', 'validation']);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasInitiatedDownload, setHasInitiatedDownload] = useState(false);

  const profile = useAppSelector(profileSelector);
  const signedPDFUrl = useAppSelector(consentFormLinkSelector);
  const isBusy = useAppSelector(isBusySelector);
  const isConsentBusy = useAppSelector(isConsentFormBusySelector);
  const hasError = useAppSelector(hasErrorSelector);
  const hasConsentError = useAppSelector(hasConsentFormErrorSelector);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (signedPDFUrl && hasInitiatedDownload) {
      window.open(signedPDFUrl, '_blank');
    }
  }, [signedPDFUrl, hasInitiatedDownload]);

  const handleConsentFormDownload = () => {
    setHasInitiatedDownload(true);
    dispatch(getConsentFormLinkThunk());
    trackEvent({ category: 'consent-form', action: 'download' });
  };

  const handleDetailsUpdate = (values: any) => {
    setHasSubmitted(true);
    dispatch(updateMyProfileThunk(values));
  };

  return (
    <>
      <PortalLayout>
        <PageContainer
          title={t('personal.title')}
          blurb={t('personal.intro')}
          icon={
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${chartColours[2]}26`,
                color: chartColours[2],
                flexShrink: 0,
                border: 1,
                borderColor: `${chartColours[2]}55`,
              }}
            >
              <PersonOutlineOutlined sx={{ fontSize: 34 }} />
            </Box>
          }
        >
          {hasSubmitted && !isBusy && hasError && (
            <Alert
              severity="error"
              icon={<ErrorOutlineOutlined />}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: 1,
                borderColor: 'error.light',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  fontWeight: 700,
                },
              }}
            >
              {t('personal.messages.error')}
            </Alert>
          )}

          {hasSubmitted && !isBusy && !hasError && (
            <Alert
              severity="success"
              icon={<CheckCircleOutlineOutlined />}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: 1,
                borderColor: 'success.light',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  fontWeight: 700,
                },
              }}
            >
              {t('personal.messages.success')}
            </Alert>
          )}

          <DetailsUpdateForm
            onSubmit={handleDetailsUpdate}
            initialValues={{
              address: profile?.address || '',
              sk2: profile?.sk2 || '',
              landline: profile?.landline || '',
              firstName: profile?.firstName || '',
              surname: profile?.surname || '',
              postcode: profile?.postcode || '',
            }}
          />
        </PageContainer>

        <Flags authorizedFlags={[FEATURES.CONSENT_FORM]}>
          <Paper
            elevation={4}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, md: 3.5 },
                py: { xs: 2.5, md: 3 },
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2.5,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${chartColours[1]}26`,
                    color: chartColours[1],
                    flexShrink: 0,
                    border: 1,
                    borderColor: `${chartColours[1]}55`,
                  }}
                >
                  <DescriptionOutlined sx={{ fontSize: 32 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                  >
                    {t('personal.consent.title')}
                  </Typography>

                  <Typography
                    variant="body1"
                    component="p"
                    color="text.secondary"
                    sx={{
                      mt: 0.75,
                      maxWidth: 760,
                    }}
                  >
                    {t('personal.consent.para1')}
                  </Typography>

                  {!isConsentBusy && hasConsentError && hasInitiatedDownload && (
                    <Typography
                      variant="body2"
                      component="p"
                      color="error"
                      sx={{
                        mt: 1,
                        fontWeight: 700,
                      }}
                    >
                      {t('personal.consent.error')}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Button
                variant="outlined"
                color="primary"
                size="large"
                endIcon={<CloudDownloadIcon />}
                onClick={handleConsentFormDownload}
                data-testid="downloadPdf"
                disabled={isConsentBusy}
                sx={{
                  flexShrink: 0,
                  borderRadius: 999,
                  px: 2.5,
                  py: 1.2,
                  fontWeight: 900,
                  textTransform: 'none',
                  alignSelf: { xs: 'stretch', md: 'center' },
                }}
              >
                {isConsentBusy ? t('personal.downloading') : t('personal.consent.button')}
              </Button>
            </Box>
          </Paper>
        </Flags>
      </PortalLayout>

      <Loader isVisible={isBusy} label={t('personal.saving')} />
    </>
  );
}

export default PersonalScene;
